

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
    Box,
    Typography,
    IconButton,
    MenuItem,
    Snackbar,
    Alert,
} from '@mui/material';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { useAuth } from '../../auth/Auth';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import MDBox from "components/MDBox";
import CircularProgress from '@mui/material/CircularProgress';
import { currencies } from 'utils/CurrencyList';
import { useCurrency } from 'context/CurrencyContext';
import { set } from 'date-fns';
import {
    VEHICLE_NUMBER_HELPER,
    VEHICLE_NUMBER_PATTERN,
    validateOrderTransportAndDates,
} from 'utils/orderValidation';

function CloneOrderModal({
    open,
    onClose,
    order,
    orderItems,
    onSave,
    suggestions,
    productList,
    user,
    transportDetails
}) {
    const [clonedFields, setClonedFields] = useState({});
    const [clonedOrderItems, setClonedOrderItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [deals, setDeals] = useState([]);
    const [contacts, setContacts] = useState([]);
    const { axiosInstance } = useAuth();
    const [cloneFormData, setCloneFormData] = useState({});
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [availableCurrencies, setAvailableCurrencies] = useState(currencies || []);
    const statusOptions = [
        { value: 'Draft', label: 'Draft' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [transid, setTransid] = useState(transportDetails.transid || '');
    const [transname, setTransname] = useState(transportDetails.transname || '');
    const [transmode, setTransmode] = useState(transportDetails.transmode || '');
    const [vehno, setVehno] = useState(transportDetails.vehno || '');
    const [vehtype, setVehtype] = useState(transportDetails.vehtype || '');
    const [transdocno, setTransdocno] = useState(transportDetails.transdocno || '');
    const [transdocdate, setTransdocdate] = useState(transportDetails.transdocdate || '');
    const { formatCurrency, currency: systemCurrency } = useCurrency();

    const activeCurrency = order.currency || systemCurrency;

    useEffect(() => {
        if (user) {
            const currentUserAsAgent = {
                id: user.id,
                username: user.username
            };
            setSelectedAgent(currentUserAsAgent);
        }
    }, [user]);

    const combineAddress = (accountData) => {
        const shippingParts = [
            accountData?.shipping_address || '',
            accountData?.shipping_city || '',
            accountData?.shipping_postal_code || '',
            accountData?.shipping_state || '',
            accountData?.shipping_country || '',

        ].filter(part => part).join(', ');

        const billingParts = [
            accountData?.billing_address || '',
            accountData?.billing_city || '',
            accountData?.billing_postal_code || '',
            accountData?.billing_state || '',
            accountData?.billing_country || '',

        ].filter(part => part).join(', ');

        return { shippingAddress: shippingParts || '', billingAddress: billingParts || '' };
    };

    useEffect(() => {
        if (open && order) {
            const { shippingAddress, billingAddress } = combineAddress(order);
            console.log('===== CLONE MODAL OPENED =====');
            console.log('Original Order:', order);
            console.log('Original Order Items:', orderItems);

            try {
                const preparedFields = {
                    ...order,
                    order_date: order.order_date
                        ? new Date(order.order_date).toISOString().split('T')[0]
                        : null,
                    account: order.account_id ? {
                        value: order.account_id,
                        label: order.account_name || 'N/A',
                        fullData: {
                            id: order.account_id,
                            name: order.account_name
                        }
                    } : null,
                    deal: order.deal_id ? {
                        value: order.deal_id,
                        label: order.deal_name || 'N/A',
                        fullData: {
                            id: order.deal_id,
                            name: order.deal_name
                        }
                    } : null,
                    contact: order.contact_id ? {
                        value: order.contact_id,
                        label: order.contact_name || 'N/A',
                        fullData: {
                            id: order.contact_id,
                            name: order.contact_name
                        }
                    } : null,
                    shipping_instruction: order.shipping_instruction || '',
                    shipping_address: shippingAddress,
                    billing_address: billingAddress,
                    shipping_method: order.shipping_method || '',
                    delivery_date: order.delivery_date
                        ? new Date(order.delivery_date).toISOString().split('T')[0]
                        : null,
                    po_number: order.po_number || '',
                    currency: order.currency || '',
                    transid: order.transid || '',
                    transname: order.transname || '',
                    transmode: order.transmode || '',
                    vehno: order.vehno || '',
                    vehType: order.vehType || '',
                    transdocno: order.transdocno || '',
                    // transdocdate: order.transdocdate || '',
                };

                const preparedOrderItems = orderItems.map(item => {
                    const quantity = parseFloat(item.quantity) || 0;
                    const discountedPrice = parseFloat(item.discounted_price) || 0;
                    return {
                        ...item,
                        product_id: item.product_id,
                        product_name: item.product_name,
                        quantity,
                        unit_price: parseFloat(item.unit_price) || 0,
                        discount_percentage: parseFloat(item.discount_percentage) || 0,
                        tax: parseFloat(item.tax) || 0,
                        discounted_price: discountedPrice,
                        total_price: (quantity * discountedPrice).toFixed(2)
                    };
                });

                console.log('Prepared Fields:', preparedFields);
                console.log('Prepared Order Items:', preparedOrderItems);

                setClonedFields(preparedFields);
                setClonedOrderItems(preparedOrderItems);

                // Fetch contacts for the current account
                if (order.account_id) {
                    fetchContacts(order.account_id);
                }
            } catch (error) {
                console.error('Error in clone modal preparation:', {
                    message: error.message,
                    stack: error.stack,
                    order: order,
                    orderItems: orderItems
                });
            }
        }

        return () => {
            setClonedFields({});
            setClonedOrderItems([]);
        };
    }, [open, order, orderItems]);

    useEffect(() => {
        // Update state when transportDetails prop changes
        setTransid(transportDetails.transid || '');
        setTransname(transportDetails.transname || '');
        setTransmode(transportDetails.transmode || '');
        setVehno(transportDetails.vehno || '');
        setVehtype(transportDetails.vehtype || '');
        setTransdocno(transportDetails.transdocno || '');
        // setTransdocdate(transportDetails.transdocdate || '');
    }, [transportDetails]);

    const fetchAccounts = async () => {
        try {
            const response = await axiosInstance.get('/accounts/');
            const formattedAccounts = response.data.map(account => ({
                value: account.id,
                label: account.name || `Account ${account.name}`,
                fullData: account
            }));
            setAccounts(formattedAccounts);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const fetchContacts = async (accountId) => {
        try {
            if (!accountId) {
                setContacts([]);
                return;
            }

            const response = await axiosInstance.get(`/accounts/contacts/${accountId}`);
            const formattedContacts = response.data.map(contact => ({
                value: contact.id,
                label: contact.first_name
                    ? `${contact.first_name} ${contact.last_name || ''}`.trim()
                    : contact.last_name,
                fullData: contact
            }));

            setContacts(formattedContacts);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const fetchDeals = async (accountId) => {
        try {
            if (!accountId) {
                setDeals([]);
                return;
            }

            const response = await axiosInstance.get(`/accounts/deals/${accountId}`);
            const formattedDeals = response.data.map(deal => ({
                value: deal.id,
                label: deal.name || `Deal ${deal.name}`,
                fullData: deal
            }));

            setDeals(formattedDeals);
        } catch (error) {
            console.error('Error fetching deals:', error);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    useEffect(() => {
        if (selectedAccount?.id) {
            fetchContacts(selectedAccount.id);
            fetchDeals(selectedAccount.id);
        } else {
            setContacts([]);
            setDeals([]);
        }
    }, [selectedAccount]);

    const fetchAccountCurrency = async (accountId) => {
        if (!accountId) return;

        try {
            const response = await axiosInstance.get(`/accounts/${accountId}`);
            const accountData = response.data;
            const { shippingAddress, billingAddress } = combineAddress(accountData);
            if (accountData && accountData.currency) {
                setClonedFields(prev => ({
                    ...prev,
                    currency: accountData.currency,
                    shipping_address: shippingAddress,
                    billing_address: billingAddress
                }));
            }
        } catch (error) {
            console.error('Error fetching account currency:', error);
        }
    };

    const handleFieldChange = (field, value) => {
        setClonedFields(prev => {
            const updates = {
                ...prev,
                [field]: value,
            };

            if (field === 'account') {
                updates.deal = null;
                updates.contact = null;
                updates.account_name = value?.label || '';

                if (value?.value) {
                    fetchContacts(value.value);
                    fetchDeals(value.value);
                    fetchAccountCurrency(value.value).then(() => {
                        axiosInstance.get(`/accounts/${value.value}`).then(response => {
                            const accountData = response.data;
                            const { shippingAddress, billingAddress } = combineAddress(accountData);
                            setClonedFields(prevFields => ({
                                ...prevFields,
                                shipping_address: shippingAddress,
                                billing_address: billingAddress
                            }));
                        }).catch(error => {
                            console.error('Error fetching account details:', error);
                        });
                    });
                } else {
                    setContacts([]);
                    setDeals([]);
                }
            }

            if (field === 'deal') {
                updates.deal_name = value?.label || '';
            }
            if (field === 'contact') {
                updates.contact_name = value?.label || '';
            }

            return updates;
        });
    };

    const formatIndianNumber = (number) => {
        if (isNaN(number) || number === null || number === undefined) return "0.00";

        const [integerPart, decimalPart] = Number(number).toFixed(2).split(".");
        const lastThreeDigits = integerPart.slice(-3);
        const otherDigits = integerPart.slice(0, -3);

        let formattedNumber = "";
        if (otherDigits) {
            formattedNumber = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThreeDigits;
        } else {
            formattedNumber = lastThreeDigits;
        }

        return `${formattedNumber}.${decimalPart}`;
    };

    const handleOrderItemChange = (index, field, value) => {
        const updatedItems = [...clonedOrderItems];
        const item = updatedItems[index];
        const originalValues = { ...item };

        if (field === 'product_name') {
            const selectedProduct = productList.find(p => p.name === value);
            if (selectedProduct) {
                item.product_name = value;
                if (item.unit_price !== selectedProduct.price) {
                    item.unit_price = selectedProduct.price || 0;
                }
                if (item.tax !== selectedProduct.tax_rate) {
                    item.tax = selectedProduct.tax_rate || 0;
                }

                if (selectedProduct.product_type === 'Service' || selectedProduct.product_type === 'Digital') {
                    item.product_type = selectedProduct.product_type;
                } else {
                    item.product_type = selectedProduct.product_type || 'Physical';
                }
            } else {
                item.product_name = "";
                item.unit_price = 0;
                item.tax = 0;
                item.quantity = 1;
                item.discount_percentage = 0;
                item.discounted_price = 0;
                item.total_price = 0;
                item.product_type = null;
            }
        } else if (field === 'quantity') {
            const product = productList.find(p => p.name === item.product_name);

            if (product && (product.product_type === 'Service' || product.product_type === 'Digital')) {
                setSnackbar({
                    open: true,
                    message: `Fixed quantity of 1 for ${product.product_type} product: ${product.name}`,
                    severity: 'info'
                });
                item.quantity = 1;
            } else {
                item[field] = value === '' ? 0 : parseFloat(value);
            }
        } else {
            item[field] = field === 'product_name'
                ? value
                : (value === '' ? 0 : parseFloat(value));
        }

        // Recalculate prices whenever any field changes
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercentage = parseFloat(item.discount_percentage) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const taxRate = parseFloat(item.tax) || 0;

        // Calculate new values
        const discountAmount = unitPrice * (discountPercentage / 100);
        const discountedPrice = unitPrice - discountAmount;
        const subtotal = discountedPrice * quantity;
        const taxAmount = subtotal * (taxRate / 100);

        // Update calculated fields
        item.discounted_price = discountedPrice.toFixed(2);
        item.total_price = (subtotal + taxAmount).toFixed(2);
        item.tax_amount = taxAmount.toFixed(2);

        setClonedOrderItems(updatedItems);
    };

    const handleAddOrderItem = () => {
        const newItem = {
            product_name: '',
            quantity: 1,
            tax: 0,
            unit_price: 0,
            discount_percentage: 0,
            total_price: 0,
            discounted_price: 0
        };
        setClonedOrderItems([...clonedOrderItems, newItem]);
    };

    const handleRemoveOrderItem = (index) => {
        const updatedItems = clonedOrderItems.filter((_, i) => i !== index);
        setClonedOrderItems(updatedItems);
    };

    const calculateTotalAmount = () => {
        let subtotal = 0;
        let taxSummary = {}; // Object to store tax rates and their total amounts

        clonedOrderItems.forEach((item) => {
            // Calculate subtotal (discounted price * quantity)
            const unitPrice = parseFloat(item.unit_price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const discountPercentage = parseFloat(item.discount_percentage) || 0;
            const discountAmount = unitPrice * (discountPercentage / 100);
            const discountedPrice = unitPrice - discountAmount;
            const itemSubtotal = discountedPrice * quantity;
            subtotal += itemSubtotal;

            // Tax on discounted net (after discount).
            if (item.tax) {
                const taxRate = parseFloat(item.tax);
                const taxAmount = itemSubtotal * (taxRate / 100);
                taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
            }

            // Update item tax_amount for consistency
            const taxRate = parseFloat(item.tax) || 0;
            item.tax_amount = (itemSubtotal * (taxRate / 100)).toFixed(2);
        });

        // Calculate total tax
        const totalTax = Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);

        // Grand total is subtotal + total tax
        const grandTotal = subtotal + totalTax;

        return grandTotal;
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            console.log('========= STARTING CLONE OPERATION =========');

            console.log('Step 1: Verifying input data');
            if (!order) {
                return;
            }
            if (!orderItems || !orderItems.length) {
                return;
            }

            console.log('\nStep 2: Verifying cloned data');
            if (!clonedFields) {
                return;
            }
            if (!clonedOrderItems || !clonedOrderItems.length) {
                return;
            }

            const today = new Date().toISOString().split('T')[0];
            const orderDateForValidation = clonedFields.order_date || today;
            const fieldValidation = validateOrderTransportAndDates({
                deliveryDate: clonedFields.delivery_date,
                orderDate: orderDateForValidation,
                vehno: clonedFields.vehno,
                transmode: clonedFields.transmode,
            });
            if (!fieldValidation.valid) {
                setFieldErrors(fieldValidation.errors);
                setSnackbar({
                    open: true,
                    message: Object.values(fieldValidation.errors)[0] || 'Please fix the highlighted fields before saving.',
                    severity: 'error',
                });
                setLoading(false);
                return;
            }
            setFieldErrors({});

            const totalAmount = calculateTotalAmount();
            console.log('💰 Calculated Total Amount:', totalAmount);

            const {
                order_number,
                id,
                created_at,
                updated_at,
                created_by,
                updated_by,
                created_by_name,
                updated_by_name,
                ...cleanedFields
            } = clonedFields;

            const payload = {
                order: {
                    ...cleanedFields,
                    total_amount: totalAmount,
                    subtotal: clonedOrderItems.reduce((acc, item) => {
                        const unitPrice = parseFloat(item.unit_price) || 0;
                        const quantity = parseFloat(item.quantity) || 0;
                        const discountPercentage = parseFloat(item.discount_percentage) || 0;
                        const discountAmount = unitPrice * (discountPercentage / 100);
                        const discountedPrice = unitPrice - discountAmount;
                        return acc + (discountedPrice * quantity);
                    }, 0),
                    is_cloned: true,
                    order_id: order.id,
                    status: 'Draft',
                    payment_status: null,
                    order_date: cleanedFields.order_date || today,
                    vehno: fieldValidation.normalizedVehno,
                    transdocno: cleanedFields.transdocno,
                    transdocdate: cleanedFields.transdocdate,
                },
                orderItems: clonedOrderItems.map(item => {
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const discountPercentage = parseFloat(item.discount_percentage) || 0;
                    const discountedPrice = item.discounted_price || (unitPrice - (unitPrice * (discountPercentage / 100)));
                    const { id: itemId, total_discounted_price, tax_amount, ...itemWithoutId } = item;
                    return {
                        ...itemWithoutId,
                        total_price: item.total_price,
                        discounted_price: item.discounted_price
                    };
                }),
                account_id: clonedFields.account?.value,
                account_name: clonedFields.account?.label,
                deal_id: clonedFields.deal?.value,
                deal_name: clonedFields.deal?.label,
                contact_id: clonedFields.contact?.value,
                contact_name: clonedFields.contact?.label,
                user: {
                    id: user?.id,
                    username: user?.username
                }
            };
            console.log('checking user data is sending in payload or not', payload);

            await onSave(payload);
            console.log('✅ Save operation completed successfully');
            onClose();

        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clonedFields.contact && contacts.length > 0) {
            const found = contacts.find(c => c.value === (clonedFields.contact?.value || clonedFields.contact));
            if (!found) {
                setClonedFields(prev => ({ ...prev, contact: contacts[0] }));
            }
        }
    }, [contacts]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                <MDTypography variant="h6">Clone Order</MDTypography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={accounts}
                                    getOptionLabel={(option) => option.label}
                                    value={accounts.find(acc => acc.value === (clonedFields.account?.value || clonedFields.account)) || null}
                                    onChange={(e, newValue) => handleFieldChange('account', newValue)}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.value === value?.value
                                    }
                                    renderInput={(params) =>
                                        <TextField {...params} label="Account"
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    height: "45px"
                                                }
                                            }}
                                        />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={deals}
                                    getOptionLabel={(option) => option.label}
                                    value={clonedFields.deal || null}
                                    onChange={(e, newValue) => handleFieldChange('deal', newValue)}
                                    renderInput={(params) => <TextField {...params} label="Deal" sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }} />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} mt={2}>
                                <Autocomplete
                                    options={contacts}
                                    getOptionLabel={(option) => option.label}
                                    value={contacts.find(c => c.value === (clonedFields.contact?.value || clonedFields.contact)) || null}
                                    onChange={(e, newValue) => handleFieldChange('contact', newValue)}
                                    renderInput={(params) => <TextField {...params} label="Contact" sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }} />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={statusOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={statusOptions.find(s => s.value === clonedFields.status) || null}
                                    onChange={(e, newValue) => handleFieldChange('status', newValue?.value || 'Draft')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Status"
                                            fullWidth
                                            margin="normal"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    height: "45px"
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="PO Number"
                                    value={clonedFields.po_number || ''}
                                    onChange={(e) => handleFieldChange('po_number', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Shipping Address"
                                    value={clonedFields.shipping_address || ''}
                                    onChange={(e) => handleFieldChange('shipping_address', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={availableCurrencies}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={availableCurrencies.find(c => c.code === clonedFields.currency) || null}
                                    onChange={(e, newValue) => handleFieldChange('currency', newValue?.code || '')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Currency"
                                            fullWidth
                                            margin="normal"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    height: "45px"
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Billing Address"
                                    value={clonedFields.billing_address || ''}
                                    onChange={(e) => handleFieldChange('billing_address', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Shipping Method"
                                    value={clonedFields.shipping_method || ''}
                                    onChange={(e) => handleFieldChange('shipping_method', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Delivery Date"
                                    type="date"
                                    value={clonedFields.delivery_date || ''}
                                    onChange={(e) => {
                                        handleFieldChange('delivery_date', e.target.value);
                                        setFieldErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.delivery_date;
                                            return next;
                                        });
                                    }}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: clonedFields.order_date || undefined,
                                    }}
                                    error={!!fieldErrors.delivery_date}
                                    helperText={fieldErrors.delivery_date || ''}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <TextField
                                    label="Notes"
                                    value={clonedFields.shipping_instruction || ''}
                                    onChange={(e) => handleFieldChange('shipping_instruction', e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <MDBox mt={2}>
                                <MDTypography variant="h6" gutterBottom>
                                    Custom Fields
                                </MDTypography>
                                <CustomFieldsSection
                                    objectType="orders"
                                    objectData={cloneFormData}
                                    isEditing={true}
                                    onFieldUpdate={(field, value) => {
                                        console.log('Updating custom field:', field, value);
                                        setCloneFormData(prev => ({
                                            ...prev,
                                            [field]: value
                                        }));
                                    }}
                                />
                            </MDBox>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ m: 1 }}>
                        <Grid item xs={12}>
                            <MDTypography variant="h6" gutterBottom>
                                Transport Details
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Transporter ID"
                                value={clonedFields.transid || ''}
                                onChange={(e) => handleFieldChange('transid', e.target.value)}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Transporter Name"
                                value={clonedFields.transname || ''}
                                onChange={(e) => handleFieldChange('transname', e.target.value)}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Transport Mode"
                                value={clonedFields.transmode || ''}
                                onChange={(e) => handleFieldChange('transmode', e.target.value)}
                                fullWidth
                                margin="normal"
                                select
                                InputProps={{
                                    // ...params.InputProps,
                                    sx: {
                                      height: '45px',
                                    },
                                  }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="1">Road</MenuItem>
                                <MenuItem value="2">Rail</MenuItem>
                                <MenuItem value="3">Air</MenuItem>
                                <MenuItem value="4">Ship</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Conditional rendering based on transport mode */}
                        {clonedFields.transmode === '1' && ( // For Road
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Vehicle Number"
                                        value={clonedFields.vehno || ''}
                                        onChange={(e) => {
                                            handleFieldChange('vehno', e.target.value.toUpperCase());
                                            setFieldErrors((prev) => {
                                                const next = { ...prev };
                                                delete next.vehno;
                                                return next;
                                            });
                                        }}
                                        fullWidth
                                        margin="normal"
                                        helperText={fieldErrors.vehno || VEHICLE_NUMBER_HELPER}
                                        error={!!fieldErrors.vehno}
                                        inputProps={{
                                            pattern: VEHICLE_NUMBER_PATTERN,
                                            style: { textTransform: 'uppercase' },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Vehicle Type"
                                        value={clonedFields.vehtype || ''}
                                        onChange={(e) => handleFieldChange('vehtype', e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        select
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '45px',
                                            },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value="R">Regular</MenuItem>
                                        <MenuItem value="C">Container</MenuItem>
                                        <MenuItem value="O">Over Dimensional Cargo</MenuItem>
                                    </TextField>
                                </Grid>
                            </>
                        )}
                        {clonedFields.transmode === '2' && ( // For Rail
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Number"
                                        value={clonedFields.transdocno || ''}
                                        onChange={(e) => handleFieldChange('transdocno', e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Date"
                                        type="date"
                                        value={transdocdate}
                                        onChange={(e) => setTransdocdate(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid> */}
                            </>
                        )}
                        {clonedFields.transmode === '3' && ( // For Air
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Number"
                                        value={clonedFields.transdocno || ''}
                                        onChange={(e) => handleFieldChange('transdocno', e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Date"
                                        type="date"
                                        value={transdocdate}
                                        onChange={(e) => setTransdocdate(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid> */}
                            </>
                        )}
                        {clonedFields.transmode === '4' && ( // For Ship
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Vehicle Number"
                                        value={clonedFields.vehno || ''}
                                        onChange={(e) => {
                                            handleFieldChange('vehno', e.target.value.toUpperCase());
                                            setFieldErrors((prev) => {
                                                const next = { ...prev };
                                                delete next.vehno;
                                                return next;
                                            });
                                        }}
                                        fullWidth
                                        margin="normal"
                                        helperText={fieldErrors.vehno || VEHICLE_NUMBER_HELPER}
                                        error={!!fieldErrors.vehno}
                                        inputProps={{
                                            pattern: VEHICLE_NUMBER_PATTERN,
                                            style: { textTransform: 'uppercase' },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Number"
                                        value={clonedFields.transdocno || ''}
                                        onChange={(e) => handleFieldChange('transdocno', e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Date"
                                        type="date"
                                        value={transdocdate}
                                        onChange={(e) => setTransdocdate(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid> */}
                            </>
                        )}
                    </Grid>
                </Grid>

                <MDBox
                    sx={{
                        mt: 3,
                        mb: 3,
                        border: "1px solid #dee2e6",
                        borderRadius: "0.75rem",
                        overflow: "auto",
                        maxHeight: "400px",
                    }}
                >
                    <MDBox
                        sx={{
                            display: "flex",
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #dee2e6",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <MDBox width="25%" p={2}>
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Product Name
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Unit Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="8%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Tax %
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Discount %
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Quantity
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Discounted Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Total Net Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="9%" p={2}>
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Actions
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                    <MDBox>
                        {clonedOrderItems.map((item, index) => (
                            <MDBox
                                key={index}
                                display="flex"
                                sx={{
                                    borderBottom: "1px solid #dee2e6",
                                    "&:hover": { backgroundColor: "#f8f9fa" },
                                }}
                            >
                                <MDBox width="25%" p={2}>
                                    <Autocomplete
                                        value={item.product_name}
                                        options={suggestions.names}
                                        onChange={(e, newValue) =>
                                            handleOrderItemChange(index, "product_name", newValue)
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": { borderColor: "#dee2e6" },
                                                        "&:hover fieldset": { borderColor: "#7b809a" },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </MDBox>
                                <MDBox width="12%" p={2} textAlign="right">
                                    <MDTypography variant="button" fontWeight="regular">
                                        {formatCurrency(
                                            item.unit_price && !isNaN(item.unit_price)
                                                ? parseFloat(item.unit_price)
                                                : 0,
                                            activeCurrency
                                        )}
                                    </MDTypography>
                                </MDBox>
                                <MDBox width="8%" p={2} textAlign="right">
                                    <MDTypography variant="button" fontWeight="regular">
                                        {item.tax || 0}%
                                    </MDTypography>
                                </MDBox>
                                <MDBox width="12%" p={2} textAlign="right">
                                    <TextField
                                        type="number"
                                        value={item.discount_percentage || 0}
                                        onChange={(e) =>
                                            handleOrderItemChange(index, "discount_percentage", e.target.value)
                                        }
                                        size="small"
                                        sx={{
                                            width: "70px",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#dee2e6" },
                                                "&:hover fieldset": { borderColor: "#7b809a" },
                                            },
                                        }}
                                    />
                                </MDBox>
                                <MDBox width="12%" p={2} textAlign="right">
                                    <TextField
                                        type="number"
                                        value={item.quantity || 0}
                                        onChange={(e) =>
                                            handleOrderItemChange(index, "quantity", e.target.value)
                                        }
                                        size="small"
                                        sx={{
                                            width: "70px",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#dee2e6" },
                                                "&:hover fieldset": { borderColor: "#7b809a" },
                                            },
                                        }}
                                    />
                                </MDBox>
                                <MDBox width="12%" p={2} textAlign="right">
                                    <MDTypography variant="button" fontWeight="regular">
                                        {formatCurrency(item.discounted_price || 0, activeCurrency)}
                                    </MDTypography>
                                </MDBox>
                                <MDBox width="12%" p={2} textAlign="right">
                                    <MDTypography variant="button" fontWeight="regular">
                                        {formatCurrency(
                                            item.total_price ? parseFloat(item.total_price) : 0,
                                            activeCurrency
                                        )}
                                    </MDTypography>
                                </MDBox>
                                <MDBox width="9%" p={2} display="flex" alignItems="center">
                                    <IconButton
                                        color="error"
                                        onClick={() => handleRemoveOrderItem(index)}
                                        size="small"
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <IconButton color="info" onClick={handleAddOrderItem} size="small">
                                        <AddIcon />
                                    </IconButton>
                                </MDBox>
                            </MDBox>
                        ))}
                    </MDBox>
                </MDBox>

                <MDBox
                    sx={{
                        mt: 3,
                        p: 2,
                        borderTop: "2px solid #dee2e6",
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <MDBox sx={{ minWidth: "300px" }}>
                        {(() => {
                            const summary = calculateTotalAmount();
                            const subtotal = clonedOrderItems.reduce((acc, item) => {
                                const unitPrice = parseFloat(item.unit_price) || 0;
                                const quantity = parseFloat(item.quantity) || 0;
                                const discountPercentage = parseFloat(item.discount_percentage) || 0;
                                const discountAmount = unitPrice * (discountPercentage / 100);
                                const discountedPrice = unitPrice - discountAmount;
                                return acc + (discountedPrice * quantity);
                            }, 0);

                            const taxSummary = clonedOrderItems.reduce((acc, item) => {
                                const unitPrice = parseFloat(item.unit_price) || 0;
                                const quantity = parseFloat(item.quantity) || 0;
                                const discountPercentage = parseFloat(item.discount_percentage) || 0;
                                const taxRate = parseFloat(item.tax) || 0;
                                const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
                                const taxAmount = (discountedPrice * quantity) * (taxRate / 100);

                                if (taxRate) {
                                    acc[taxRate] = (acc[taxRate] || 0) + taxAmount;
                                }
                                return acc;
                            }, {});

                            return (
                                <MDBox>
                                    <MDBox display="flex" justifyContent="space-between" mb={1}>
                                        <MDTypography variant="button" color="text" fontWeight="regular">
                                            Subtotal:
                                        </MDTypography>
                                        <MDTypography variant="button" color="text" fontWeight="medium">
                                            {formatCurrency(subtotal, activeCurrency)}
                                        </MDTypography>
                                    </MDBox>

                                    {Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                                        <MDBox
                                            key={taxRate}
                                            display="flex"
                                            justifyContent="space-between"
                                            mb={1}
                                        >
                                            <MDTypography variant="button" color="text" fontWeight="regular">
                                                Tax ({taxRate}%):
                                            </MDTypography>
                                            <MDTypography variant="button" color="text" fontWeight="medium">
                                                {formatCurrency(taxAmount, activeCurrency)}
                                            </MDTypography>
                                        </MDBox>
                                    ))}

                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        mt={2}
                                        pt={1.5}
                                        sx={{ borderTop: "1px solid #dee2e6" }}
                                    >
                                        <MDTypography variant="button" color="dark" fontWeight="medium">
                                            Total Amount:
                                        </MDTypography>
                                        <MDTypography variant="button" color="info" fontWeight="bold">
                                            {formatCurrency(summary, activeCurrency)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        })()}
                    </MDBox>
                </MDBox>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <MDButton onClick={onClose} variant="outlined" color="secondary">
                    Cancel
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : "Save"}
                </MDButton>
            </DialogActions>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity || 'info'}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

export default CloneOrderModal;